import { ROLES } from '@shared/constants/roles';
import { STORAGE_KEYS } from '@shared/constants/storage-keys';
import { storage } from '@shared/utils/storage';
import type { AuthSession, AuthUser, SavedAccount } from '@shared/types/auth';

/**
 * Saved-account persistence.
 *
 * Only metadata is stored — never an access token, refresh token or password.
 * Reads go through the existing fail-soft `storage` helper, so a corrupt or
 * unavailable localStorage degrades to "no saved accounts" instead of throwing
 * during boot.
 */

/** Upper bound on concurrent accounts; keeps the switcher and cookie count sane. */
export const MAX_ACCOUNTS = 5;

export function readAccounts(): SavedAccount[] {
  const raw = storage.get<SavedAccount[]>(STORAGE_KEYS.ACCOUNTS);
  if (!Array.isArray(raw)) return [];
  return raw.filter((account) => Boolean(account?.userId));
}

export function writeAccounts(accounts: SavedAccount[]): void {
  storage.set(STORAGE_KEYS.ACCOUNTS, accounts);
}

export function readActiveAccountId(): string | null {
  return storage.get<string>(STORAGE_KEYS.ACTIVE_ACCOUNT) ?? null;
}

export function writeActiveAccountId(userId: string | null): void {
  if (userId) storage.set(STORAGE_KEYS.ACTIVE_ACCOUNT, userId);
  else storage.remove(STORAGE_KEYS.ACTIVE_ACCOUNT);
}

/** Build the stored record from an authenticated session. */
export function toSavedAccount(
  user: AuthUser,
  session?: AuthSession | null,
  previous?: SavedAccount,
): SavedAccount {
  const now = new Date().toISOString();
  return {
    userId: user.id,
    sessionId: session?.sessionId ?? previous?.sessionId ?? null,
    email: user.email,
    displayName: user.employee?.display_name || user.email || user.id,
    isOwner: Boolean(user.isOwner ?? previous?.isOwner),
    roles: user.roles ?? previous?.roles ?? [],
    addedAt: previous?.addedAt ?? now,
    lastUsedAt: now,
  };
}

/**
 * Insert or update by `user.id`, so signing in again as someone already saved
 * refreshes their record (new session id, latest roles) instead of creating a
 * duplicate entry.
 */
export function upsertAccount(accounts: SavedAccount[], account: SavedAccount): SavedAccount[] {
  const index = accounts.findIndex((entry) => entry.userId === account.userId);
  if (index === -1) return [...accounts, account];
  const next = [...accounts];
  next[index] = { ...next[index], ...account };
  return next;
}

export function removeAccount(accounts: SavedAccount[], userId: string): SavedAccount[] {
  return accounts.filter((account) => account.userId !== userId);
}

export function findAccount(accounts: SavedAccount[], userId: string | null): SavedAccount | null {
  if (!userId) return null;
  return accounts.find((account) => account.userId === userId) ?? null;
}

/**
 * Is this account an owner?
 *
 * Uses the backend's own `isOwner` flag first — the same signal the permission
 * layer bypasses on — and falls back to the role name from the existing
 * `ROLES` constant for a payload that only carries `roles`. No new role model.
 */
export function isOwnerAccount(account: SavedAccount): boolean {
  return account.isOwner || account.roles.includes(ROLES.OWNER);
}

/** True when at least one saved account is an owner. Gates "Add Account". */
export function hasOwnerAccount(accounts: SavedAccount[]): boolean {
  return accounts.some(isOwnerAccount);
}
